import Highcharts from "highcharts";
import { lightest } from "@ui";
import { FailureMetadata, FAILURE_METADATA } from "../PointMetadata";
import t from "../../../../i18n";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Failure, Setup } from "../../../../api/useSetups";
import type { Index } from "diagnostics-lib/iterable";
import { indexBy } from "diagnostics-lib/iterable";
import { inv } from "../../../../lib/chiSquared";
import { getMostRecentDisplayNames } from "../../../../domain/failures";
import type { FailureValue, IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { getFailureColor } from "../../useFailureColor";
import {
  type FailureValueStatus,
  type IndexedFailureMetadata,
} from "../../../../api/useFailureMetadata";
import { NONE_LABEL_FAILURE_STATUS } from "../../../../api/useFailureMetadata";
import { getAnalysisSetup, getTimeSetup } from "diagnostics-domain/setup/getSetup";
import type { Quantity } from "../../../../api/useQuantities";
import { CLASSIC_FAILURE } from "./serieIds";

export const failureStatusDescription = (status: FailureValueStatus): string => {
  switch (status) {
    case "TO_BE_ANALYSED":
    case "BACK_TO_BE_ANALYSED":
      return t("summary.chart.evolution.tooltip.description.to_be_analysed");
    case "SUSPECTED":
      return t("summary.chart.evolution.tooltip.description.suspected");
    case "CONFIRMED":
      return t("summary.chart.evolution.tooltip.description.confirmed");
    case "REJECTED":
      return t("summary.chart.evolution.tooltip.description.false_detection");
    case "LEGACY_REJECTED":
      return t("summary.chart.evolution.tooltip.description.false_detection");
    default:
      throw new Error(status satisfies never);
  }
};

Highcharts.SVGRenderer.prototype.symbols["circleDash"] = function (
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const dashWidth = 25;
  const gapWidth = 19;
  const radius = Math.min(w, h) / 2 + 0.8;
  const pointArray = [];

  for (let angle = 0; angle < 360; angle += dashWidth + gapWidth) {
    const startX = x + w / 2 + radius * Math.cos((angle * Math.PI) / 180);
    const startY = y + h / 2 + radius * Math.sin((angle * Math.PI) / 180);
    const endX = x + w / 2 + radius * Math.cos(((angle + dashWidth) * Math.PI) / 180);
    const endY = y + h / 2 + radius * Math.sin(((angle + dashWidth) * Math.PI) / 180);
    pointArray.push("M", startX, startY, "L", endX, endY);
  }

  return pointArray;
};

const TO_BE_ANALYSED_STATES = {
  select: {
    radius: 8,
    fillColor: undefined,
    lineColor: undefined,
    lineWidth: 6,
  },
};

const NOT_ENABLED_MARKER = {
  enabled: false,
};

export const drawMarker = (status: FailureValueStatus, color: string) => {
  switch (status) {
    case "TO_BE_ANALYSED":
      return {
        symbol: "circle",
        lineWidth: 4,
        lineColor: color,
        fillColor: lightest(color),
        states: TO_BE_ANALYSED_STATES,
      };
    case "SUSPECTED":
      return {
        symbol: "circle",
        lineWidth: 2,
        lineColor: color,
        fillColor: lightest(color),
      };
    case "CONFIRMED":
    case "REJECTED":
    case "LEGACY_REJECTED":
      return {
        symbol: "circle",
        lineWidth: 2,
        lineColor: color,
        fillColor: color,
      };
    default:
      return NOT_ENABLED_MARKER;
  }
};

const getObservedValue = (
  failureValue: FailureValue,
  qoi: Quantity | null,
  observableByQuantityId: Index<Setup["diagnosisInputs"][number]>,
  impactedObsCount: number,
) => {
  if (qoi === null) {
    let impactOnQoi = 0;

    for (const quantityId in failureValue.impacts) {
      const value = failureValue.impacts[quantityId];
      if (value === undefined) throw new Error(`Impact value not found for quantity ${quantityId}`);
      const obs = observableByQuantityId.get(quantityId);
      if (!obs || obs.enabled === false || obs.sigma === 0) continue;

      impactOnQoi += Math.pow(value / obs.sigma, 2);
    }

    return Math.sqrt(impactOnQoi / inv(0.95, impactedObsCount)) * -1;
  }

  const impactOnQoi = failureValue.impacts[qoi.id];

  if (impactOnQoi === undefined) return null;

  return impactOnQoi;
};

export function getFilteredFailureValue(
  failureName: string,
  analysisDate: number,
  indexedFailureValues: IndexedFailureValues,
  failureValueStatus: FailureValueStatus,
  failureValueStatusFilter: Set<FailureValueStatus>,
) {
  if (failureValueStatusFilter.size) {
    if (failureValueStatusFilter.has(failureValueStatus)) {
      return indexedFailureValues[analysisDate]?.[failureName];
    }
    return;
  }

  return indexedFailureValues[analysisDate]?.[failureName];
}

const notRejectedStatusSet = new Set<FailureValueStatus>([
  "TO_BE_ANALYSED",
  "SUSPECTED",
  "CONFIRMED",
]);

const classicMarker = {
  states: {
    select: {
      radius: 8,
      fillColor: undefined,
      lineColor: undefined,
      lineWidth: 4,
    },
  },
};

const emptyMarker = { symbol: "empty" };

export function getFailureSerie({
  type,
  failureSerieType,
  indexedFailureValues,
  indexedFailureMetadata,
  yAxisId,
  failureName,
  analyses,
  setups,
  displayNameByFailureName,
  qoi,
  legendIndex,
  color,
  failureByNameBySetupId,
  observableByQuantityIdBySetupId,
  impactedObsCountByAnalysisId,
  actionAllowed,
  failureValueStatusFilter,
}: {
  type: "line" | "area";
  failureSerieType: "OBSERVABLE" | "AMPLITUDE";
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  yAxisId: string;
  failureName: string;
  analyses: ActiveAnalysis[];
  setups: Setup[];
  displayNameByFailureName: Record<string, string>;
  qoi: Quantity | null;
  legendIndex: number;
  color: string;
  failureByNameBySetupId: Index<{ setupId: string; failureByName: Index<Failure> }>;
  observableByQuantityIdBySetupId: Index<{
    setupId: string;
    observableByQuantityId: Index<Setup["diagnosisInputs"][number]>;
  }>;
  impactedObsCountByAnalysisId: Index<{ analysisId: string; count: number }>;
  actionAllowed: boolean;
  failureValueStatusFilter: Set<FailureValueStatus>;
}): Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions {
  let nextDone = false;

  const data = analyses.reduce<Highcharts.PointOptionsObject[]>((acc, analysis, i) => {
    const previousAnalysis = analyses[i - 1];
    const nextAnalysis = analyses[i + 1];

    const setup = getAnalysisSetup(setups, analysis);
    if (setup === null) return acc;
    const failure = failureByNameBySetupId.get(setup.id)?.failureByName.get(failureName) ?? null;
    if (!failure) return acc;

    const failureMetadataStatus = indexedFailureMetadata.getFailureStatus(
      analysis.date,
      failureName,
    );
    const failureMetadataReparability = indexedFailureMetadata.getFailureReparabilty(
      analysis.date,
      failureName,
    );

    const failureValue = getFilteredFailureValue(
      failure?.name,
      analysis.date,
      indexedFailureValues,
      failureMetadataStatus,
      failureValueStatusFilter,
    );
    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      const custom: FailureMetadata = {
        type: FAILURE_METADATA,
        group: null,
        label: null,
        analysisId: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        analysisDate: analysis.date,
        failureName: null,
        failureDisplayName: failure.displayName,
        failureStatus: null,
        failureAmplitude: null,
        failureUnit: null,
        failureDescription: null,
        confidenceIndicator: null,
        filters: null,
        human: null,
        reparability: null,
        quantityValues: null,
      };

      acc.push({
        id: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        x: (analysis.date + previousAnalysis.date) / 2,
        name: failure.name,
        y: null,
        custom,
        allowPointSelect: false,
      });
    }

    if (nextDone) {
      nextDone = false;
      return acc;
    }

    const previousSetupId = previousAnalysis && getAnalysisSetup(setups, previousAnalysis)?.id;
    const previousFailure = previousSetupId
      ? failureByNameBySetupId.get(previousSetupId)?.failureByName.get(failureName)
      : undefined;
    const previousFailureValueStatus =
      previousAnalysis?.date === undefined
        ? NONE_LABEL_FAILURE_STATUS
        : indexedFailureMetadata.getFailureStatus(previousAnalysis?.date, failureName);
    const previousFailureValue =
      previousFailure?.id && previousAnalysis?.id
        ? getFilteredFailureValue(
            previousFailure?.name,
            previousAnalysis?.date,
            indexedFailureValues,
            previousFailureValueStatus,
            failureValueStatusFilter,
          )
        : undefined;
    const previousImpactOnQoi = qoi === null ? undefined : previousFailureValue?.impacts[qoi.id];

    const nextSetupId = nextAnalysis && getAnalysisSetup(setups, nextAnalysis)?.id;
    const nextFailure = nextSetupId
      ? failureByNameBySetupId.get(nextSetupId)?.failureByName.get(failureName)
      : undefined;
    const nextFailureValueStatus =
      nextAnalysis?.date === undefined
        ? NONE_LABEL_FAILURE_STATUS
        : indexedFailureMetadata.getFailureStatus(nextAnalysis?.date, failureName);
    const nextFailureValue =
      nextFailure?.id && nextAnalysis?.id
        ? getFilteredFailureValue(
            nextFailure.name,
            nextAnalysis.date,
            indexedFailureValues,
            nextFailureValueStatus,
            failureValueStatusFilter,
          )
        : undefined;
    const nextImpactOnQoi = qoi === null ? undefined : nextFailureValue?.impacts[qoi.id];

    const isCheckFailureValueStatusFilter =
      !failureValueStatusFilter.size || failureValueStatusFilter.has(failureMetadataStatus);
    if (
      (!failureValue && failureMetadataStatus === "REJECTED" && isCheckFailureValueStatusFilter) ||
      (failureValue &&
        failureMetadataStatus === "LEGACY_REJECTED" &&
        isCheckFailureValueStatusFilter)
    ) {
      const custom: FailureMetadata = {
        type: FAILURE_METADATA,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        analysisDate: analysis.date,
        failureName: failure.name,
        failureDisplayName: failure.displayName,
        failureStatus: failureMetadataStatus,
        failureAmplitude: null,
        failureUnit: null,
        failureDescription: failure.description,
        confidenceIndicator: null,
        filters: failure.filters,
        quantityValues: analysis.quantityValues,
        human: null,
        reparability: null,
      };

      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: 0,
        custom,
        marker: drawMarker(failureMetadataStatus, color),
        allowPointSelect: actionAllowed,
      });
    } else if (
      analysis.status === "DIAGNOSIS" &&
      !failureValue &&
      nextFailureValue &&
      notRejectedStatusSet.has(nextFailureValueStatus)
    ) {
      const custom: FailureMetadata = {
        type: FAILURE_METADATA,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        analysisDate: analysis.date,
        failureName: null,
        failureDisplayName: failure.displayName,
        failureStatus: null,
        failureAmplitude: null,
        failureUnit: null,
        failureDescription: null,
        confidenceIndicator: null,
        filters: null,
        quantityValues: null,
        human: null,
        reparability: null,
      };
      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: nextImpactOnQoi === 0 ? null : 0,
        marker: emptyMarker,
        custom,
        allowPointSelect: false,
      });
    } else if (failureValue && notRejectedStatusSet.has(failureMetadataStatus)) {
      const observableByQuantityId = observableByQuantityIdBySetupId.get(setup.id);
      const count = impactedObsCountByAnalysisId.get(analysis.id);

      if (!observableByQuantityId) throw new Error(`Observable not found for setup ${setup.id}`);

      if (!count) throw new Error(`Impacted obs count not found for analysis ${analysis.id}`);

      const observedValue = getObservedValue(
        failureValue,
        qoi,
        observableByQuantityId.observableByQuantityId,
        count.count,
      );

      const unit = qoi ? qoi.unit : failure.unit; //Debatable ?

      const observableValue = observedValue ?? 0;
      const amplitudeValue = failureValue.value ?? 0;

      const custom: FailureMetadata = {
        type: FAILURE_METADATA,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        analysisDate: analysis.date,
        failureName: failure.name,
        failureDisplayName: failure.displayName,
        failureStatus: failureMetadataStatus,
        failureAmplitude: amplitudeValue,
        failureUnit: failure.unit,
        failureDescription: failure.description,
        confidenceIndicator: failureValue.confidence,
        filters: failure.filters,
        quantityValues: analysis.quantityValues,
        human: {
          value: failureSerieType === "OBSERVABLE" ? observableValue : amplitudeValue,
          unit: failureSerieType === "OBSERVABLE" ? (unit ?? "") : (failure.unit ?? ""),
          precision: 2,
          sign: qoi !== null,
        },
        reparability: failureMetadataReparability ?? failure.reparability,
      };

      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: failureSerieType === "OBSERVABLE" ? observableValue : amplitudeValue,
        custom,
        marker: drawMarker(failureMetadataStatus, color),
        allowPointSelect: actionAllowed,
      });

      if (
        notRejectedStatusSet.has(failureMetadataStatus) &&
        nextAnalysis?.status === "DIAGNOSIS" &&
        analysis.group === nextAnalysis?.group &&
        !nextFailureValue &&
        notRejectedStatusSet.has(nextFailureValueStatus)
      ) {
        nextDone = true;
        const custom: FailureMetadata = {
          type: FAILURE_METADATA,
          group: analysis.group,
          label: analysis.label,
          analysisId: analysis.id,
          analysisDate: analysis.date,
          failureName: null,
          failureDisplayName: failure.displayName,
          failureStatus: null,
          failureAmplitude: null,
          failureUnit: null,
          failureDescription: failure.description,
          confidenceIndicator: null,
          filters: null,
          quantityValues: null,
          human: null,
          reparability: null,
        };

        acc.push({
          id: `analysis-id-${nextAnalysis.id}`,
          name: failure.name,
          x: nextAnalysis.date,
          y:
            (failureSerieType === "OBSERVABLE" ? observedValue : amplitudeValue) !== null &&
            previousImpactOnQoi !== 0
              ? 0
              : null,
          marker: emptyMarker,
          custom,
          allowPointSelect: false,
        });
      }
    } else {
      const custom: FailureMetadata = {
        type: FAILURE_METADATA,
        analysisDate: analysis.date,
        failureName: null,
        failureDisplayName: failure.displayName,
        failureStatus: null,
        failureAmplitude: null,
        failureUnit: null,
        failureDescription: failure.description,
        confidenceIndicator: null,
        filters: null,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        human: null,
        reparability: null,
        quantityValues: null,
      };
      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom,
        allowPointSelect: false,
      });
    }

    return acc;
  }, []);

  const dataZone = data.reduce<{ date: number; status: FailureValueStatus }[]>((zones, point) => {
    const custom = point.custom as FailureMetadata;
    const { analysisDate: date, failureStatus: status } = custom;
    const prevStatus = zones[zones.length - 1]?.status;
    const currentStatus = status ?? prevStatus ?? null;
    if (currentStatus === null) return zones;
    zones.push({ date, status: currentStatus });

    return zones;
  }, []);
  const uniqueDataZone = dataZone.filter(
    (zone, index, self) => index === self.length - 1 || zone.status !== dataZone[index + 1]?.status,
  );
  const zones = uniqueDataZone.map<Highcharts.SeriesZonesOptionsObject>(zone =>
    ["TO_BE_ANALYSED", "SUSPECTED"].includes(zone.status)
      ? {
          value: zone.date,
          dashStyle: "Dash",
        }
      : {
          value: zone.date,
        },
  );

  return {
    id: `${CLASSIC_FAILURE}-${failureName}`,
    yAxis: yAxisId,
    zoneAxis: "x",
    zones,
    name: failureName,
    custom: {
      failureDisplayName: displayNameByFailureName[failureName] ?? failureName,
    },
    data,
    color,
    type,
    softThreshold: false,
    showInLegend: true,
    legendIndex,
    allowPointSelect: actionAllowed,
    cursor: actionAllowed ? "pointer" : "default",
    replicateInNavigator: true,
    marker: classicMarker,
  };
}

const getFailureSeries = ({
  type,
  failureSerieType = "OBSERVABLE",
  project,
  yAxisId,
  setups,
  analyses,
  indexedFailureValues,
  indexedFailureMetadata,
  qoi,
  startLegendIndex,
  actionAllowed,
  failureValueStatusFilter,
}: {
  type: "line" | "area";
  failureSerieType: "OBSERVABLE" | "AMPLITUDE";
  project: Project;
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  qoi: Quantity | null;
  startLegendIndex: number;
  actionAllowed: boolean;
  failureValueStatusFilter: Set<FailureValueStatus>;
}): (Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions)[] => {
  const failureByNameBySetupId = indexBy(
    setups.map(setup => ({
      setupId: setup.id,
      failureByName: indexBy(setup.failures, "name"),
    })),
    "setupId",
  );

  const observableByQuantityIdBySetupId = indexBy(
    setups.map(setup => ({
      setupId: setup.id,
      observableByQuantityId: indexBy(setup.diagnosisInputs, "quantityId"),
    })),
    "setupId",
  );

  const failureNamesSet = new Set<string>();
  for (const dateStr in indexedFailureValues) {
    const date = Number(dateStr);
    const setup = getTimeSetup(setups, date);
    if (!setup) continue;
    const setupFailures = failureByNameBySetupId.get(setup.id);
    if (!setupFailures) continue;

    for (const failureName in indexedFailureValues[date]) {
      if (failureNamesSet.has(failureName)) continue;
      if (!setupFailures.failureByName.has(failureName)) continue;
      const status = indexedFailureMetadata.getFailureStatus(date, failureName);
      if (failureValueStatusFilter.size && !failureValueStatusFilter.has(status)) continue;
      failureNamesSet.add(failureName);
    }
  }

  for (const { date, failureName, status } of indexedFailureMetadata.failureValueStatus) {
    if (failureNamesSet.has(failureName)) continue;
    const setup = getTimeSetup(setups, date);
    if (!setup) continue;
    const setupFailures = failureByNameBySetupId.get(setup.id);
    if (!setupFailures) continue;
    if (!setupFailures.failureByName.has(failureName)) continue;
    if (failureNamesSet.has(failureName)) continue;
    if (failureValueStatusFilter.size && !failureValueStatusFilter.has(status)) continue;
    failureNamesSet.add(failureName);
  }

  const displayNameByFailureName = getMostRecentDisplayNames(setups);
  const failureNames = Array.from(failureNamesSet).sort((a, b) => {
    const displayNameA = displayNameByFailureName[a] ?? a;
    const displayNameB = displayNameByFailureName[b] ?? b;
    return displayNameA.localeCompare(displayNameB);
  });

  const impactedObsCounts = analyses.reduce<{ analysisId: string; count: number }[]>(
    (acc, analysis) => {
      const setup = getAnalysisSetup(setups, analysis);

      if (setup !== null) {
        const impactedObs = setup.diagnosisInputs.filter(input => {
          const quantityValue = analysis.quantityValues[input.quantityId];
          return input.enabled && quantityValue !== undefined && quantityValue.simulated !== null;
        });

        acc.push({
          analysisId: analysis.id,
          count: impactedObs.length,
        });
      }

      return acc;
    },
    [],
  );

  const impactedObsCountByAnalysisId = indexBy(impactedObsCounts, "analysisId");

  return failureNames.map((failureName, i) =>
    getFailureSerie({
      type,
      failureSerieType,
      indexedFailureValues,
      indexedFailureMetadata,
      yAxisId,
      failureName,
      color: getFailureColor(project, "FAILURE", failureName),
      analyses,
      setups,
      displayNameByFailureName,
      qoi,
      failureByNameBySetupId,
      observableByQuantityIdBySetupId,
      legendIndex: startLegendIndex + i,
      impactedObsCountByAnalysisId,
      actionAllowed,
      failureValueStatusFilter,
    }),
  );
};

export default getFailureSeries;
